import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { connect } from 'react-redux';

// Component Imports
import { clearDraftState, withAppCreateReferralContext } from '@unite-us/app-create-referral';
import featureFlag from 'common/utils/FeatureFlag/FeatureFlag';
import {
  Popover,
  Button,
  Tabs,
  Tab,
  Icon,
  CheckBoxField,
} from '@unite-us/app-components';
import ClientSummary from 'src/components/ClientSummary';
import { browserHistory } from 'common/utils/browserHistory';
import callOrLog from 'common/utils/callOrLog';
import { REFERRAL, FACESHEET } from 'common/utils/EventTracker/utils/eventConstants';
import { Serializer } from '@unite-us/client-utils';
import { REFERRALS_CREATE_REFERRALS } from 'common/utils/FeatureFlag/utils/constants';
import {
  scrn7ScreeningsCompletion,
  hasCaseManagerRole,
  cl2372SocialCarePlanTab,
} from 'common/utils/FeatureFlags/flags';
import { hasFeatureRole } from 'common/utils/Roles/hasFeatureRole';
import { get, isEmpty } from 'lodash';
import './FacesheetSubHeader.scss';

function urlMappings(contactId) {
  const baseUrl = `/facesheet/${contactId}`;

  return {
    overview: baseUrl,
    profile: `${baseUrl}/profile`,
    cases: `${baseUrl}/cases`,
    eligibility: `${baseUrl}/eligibility`,
    forms: `${baseUrl}/forms`,
    screenings: `${baseUrl}/screenings`,
    uploads: `${baseUrl}/uploads`,
    referrals: `${baseUrl}/referrals`,
    resource: `${baseUrl}/resource-lists`,
    carePlan: `${baseUrl}/care-plan`,
  };
}

export class FacesheetSubHeader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: props.activeTab,
    };

    this.onReferClicked = this.onReferClicked.bind(this);
    this.handleTabChange = this.handleTabChange.bind(this);
    this.getTabsConfig = this.getTabsConfig.bind(this);
    this.shouldShowActionSection = this.shouldShowActionSection.bind(this);
    this.renderClientActionCheckbox = this.renderClientActionCheckbox.bind(this);
    this.renderReferButton = this.renderReferButton.bind(this);
    this.renderClientSummary = this.renderClientSummary.bind(this);
    this.renderActionSection = this.renderActionSection.bind(this);
    this.renderTabs = this.renderTabs.bind(this);
    this.renderMergeLink = this.renderMergeLink.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState({
      value: nextProps.activeTab,
    });
  }

  handleTabChange(value) {
    const { contact } = this.props;
    const faceSheetConstant = FACESHEET[value];

    browserHistory.push(urlMappings(this.props.contactId)[value]);

    if (faceSheetConstant) {
      const eventPayload = Serializer.build({ contact });
      callOrLog(() => this.context.eventTracker(faceSheetConstant, { contact: eventPayload }));
    } else {
      window.datadogLogs.logger.warn(`Untracked Facesheet Value, ${value}`);
    }
  }

  onReferClicked() {
    const { dispatchCreateReferral, contact } = this.props;

    clearDraftState(dispatchCreateReferral);

    callOrLog(() => this.context.eventTracker(
      REFERRAL.onReferButtonClicked,
      null,
      { contact },
    ));

    this.props.onButtonClick();
  }

  getTabsConfig() {
    const {
      visibilityConfig,
      scrn7ScreeningsCompletionEnabled,
      hasSocialCareNavigationRole,
      hbh1025ShowEligibilityAssessmentTab,
      carePlanTabEnabled,
      clientRelationship,
      flagEnabled,
    } = this.props;

    const showElAssessmentTab = hasSocialCareNavigationRole && hbh1025ShowEligibilityAssessmentTab;

    return {
      overview: {
        label: 'Overview',
        show: visibilityConfig.overview.showTab,
      },
      profile: {
        label: 'Profile',
        show: visibilityConfig.profile.showTab,
      },
      cases: {
        label: 'Cases',
        show: visibilityConfig.cases.showTab,
      },
      screenings: {
        label: 'Screenings',
        show: scrn7ScreeningsCompletionEnabled && visibilityConfig.screenings.showTab,
      },
      eligibility: {
        label: 'Eligibility Assessments',
        show: showElAssessmentTab && visibilityConfig.eligibility.showTab,
      },
      forms: {
        label: 'Forms',
        show: !isEmpty(clientRelationship) && visibilityConfig.forms.showTab,
      },
      uploads: {
        label: 'Uploads',
        show: visibilityConfig.uploads.showTab,
      },
      referrals: {
        label: 'Referrals',
        show: flagEnabled('referrals_view_referral_details') && visibilityConfig.referrals.showTab,
      },
      resource: {
        label: 'Resources',
        show: visibilityConfig.resource.showTab,
      },
      carePlan: {
        label: 'Care Plan',
        show: carePlanTabEnabled && visibilityConfig.carePlan.showTab,
      },
    };
  }

  shouldShowActionSection() {
    const {
      flagEnabled,
      isCaseManagerRole,
      contact,
      clientRelationship,
    } = this.props;
    return (
      (flagEnabled(REFERRALS_CREATE_REFERRALS) || isCaseManagerRole) &&
      !isEmpty(contact) &&
      !isEmpty(clientRelationship)
    );
  }

  renderClientActionCheckbox() {
    const { clientRelationship, handleOnCheck, isUpdatingRelationship } = this.props;

    return (
      <div className="flex mt-2 sm:pl-1">
        <CheckBoxField
          id="client-requires-action__checkbox"
          className="client-requires-action__checkbox"
          label="Client requires action"
          checked={clientRelationship.flagged || false}
          onChange={handleOnCheck}
          disabled={isUpdatingRelationship}
          showError={false}
          showHint={false}
          style={{ container: 'ui-form-field' }}
        />
      </div>
    );
  }

  renderReferButton() {
    const {
      contact,
      styles,
      resourceListDetailEnhancements,
      isCaseManagerRole,
    } = this.props;

    if (resourceListDetailEnhancements) {
      return (
        <div className="ml-3 facesheet-sub-header__refer-button-container">
          <Popover
            content={(
              <div>
                Find resources to share with your client or<br />
                send referrals through Unite Us.
              </div>
            )}
            id="hover-popover"
            trigger="hover"
            position="bottom"
          >
            <Button
              id="subheader-refer-btn"
              label={`Find Resources for ${contact.first_name}`}
              labelPosition={'before'}
              labelStyle={styles.referButton.label}
              iconLeft={
                <Icon icon="Search" className="fill-current" />
              }
              onClick={this.onReferClicked}
              primary
            />
          </Popover>
        </div>
      );
    }

    if (!isCaseManagerRole) {
      return (
        <div className="ml-3 facesheet-sub-header__refer-button-container">
          <Button
            id="subheader-refer-btn"
            label={`Refer ${contact.first_name}`}
            labelPosition={'before'}
            labelStyle={styles.referButton.label}
            iconRight={
              <Icon icon="Forward" className="fill-current" />
            }
            onClick={this.onReferClicked}
            primary
          />
        </div>
      );
    }

    return null;
  }

  renderClientSummary() {
    const {
      contact,
      enums,
      isMilitaryFocused,
      isFetching,
      hasClientLink,
    } = this.props;

    if (isEmpty(contact)) {
      return null;
    }

    return (
      <ClientSummary
        contact={contact}
        enums={enums}
        isMilitaryFocused={isMilitaryFocused}
        isFetching={isFetching}
        hasClientLink={hasClientLink}
      />
    );
  }

  renderActionSection() {
    if (!this.shouldShowActionSection()) {
      return null;
    }

    return (
      <div className="flex items-center col-sm-12 col-md-5 sm:justify-between md:justify-end">
        {this.renderClientActionCheckbox()}
        {this.renderReferButton()}
      </div>
    );
  }

  renderTabs() {
    const tabsConfig = this.getTabsConfig();

    return (
      <Tabs
        id="facesheet-tabs"
        className="ui-tabs facesheet-tabs"
        onChange={this.handleTabChange}
        value={this.state.value}
      >
        {Object.keys(tabsConfig).map((tab) => {
          const { label, show } = tabsConfig[tab];
          return show ? (
            <Tab
              id={`facesheet-${tab}-tab`}
              label={label}
              value={tab}
              key={tab}
              className={`ui-tab ${this.state.value === tab ? 'ui-tab--active' : ''}`}
            />
          ) : null;
        })}
      </Tabs>
    );
  }

  renderMergeLink() {
    const { showDuplicatesLink, contactId } = this.props;

    if (!showDuplicatesLink) {
      return null;
    }

    return (
      <Link
        className="font-bold text-action-blue"
        to={{
          pathname: `/people/duplicates/${contactId}`,
          query: {
            back: urlMappings(contactId)[this.state.value],
          },
        }}
      >
        Merge
      </Link>
    );
  }

  render() {
    return (
      <div className="facesheet-sub-header" id="subheader">
        <div className="row facesheet-sub-header__title-row">
          <div className="col-sm-12 col-md-7">
            <div className="facesheet-sub-header__title-summary">
              {this.renderClientSummary()}
            </div>
          </div>
          {this.renderActionSection()}
        </div>
        <section className="flex justify-between" aria-labelledby="facesheet-tabs-heading">
          <h2 className="sr-only" id="facesheet-tabs-heading">Facesheet Tabs</h2>
          {this.renderTabs()}
          <div className="flex items-center">
            {this.renderMergeLink()}
          </div>
        </section>
      </div>
    );
  }
}

const SingleVisibilityConfigPropType = PropTypes.shape({
  showTab: PropTypes.bool.isRequired,
});

FacesheetSubHeader.propTypes = {
  activeTab: PropTypes.string.isRequired,
  clientRelationship: PropTypes.object,
  contact: PropTypes.shape({
    first_name: PropTypes.string,
    full_name: PropTypes.string,
    empi_id: PropTypes.string,
  }).isRequired,
  contactId: PropTypes.string.isRequired,
  dispatchCreateReferral: PropTypes.func,
  enums: PropTypes.object.isRequired,
  flagEnabled: PropTypes.func.isRequired,
  handleOnCheck: PropTypes.func.isRequired,
  hasClientLink: PropTypes.bool.isRequired,
  isFetching: PropTypes.bool.isRequired,
  isMilitaryFocused: PropTypes.bool.isRequired,
  isUpdatingRelationship: PropTypes.bool,
  resourceListDetailEnhancements: PropTypes.bool,
  onButtonClick: PropTypes.func.isRequired,
  styles: PropTypes.object,
  showDuplicatesLink: PropTypes.bool.isRequired,
  scrn7ScreeningsCompletionEnabled: PropTypes.bool,
  hbh1025ShowEligibilityAssessmentTab: PropTypes.bool.isRequired,
  hasSocialCareNavigationRole: PropTypes.bool,
  carePlanTabEnabled: PropTypes.bool,
  visibilityConfig: PropTypes.shape({
    overview: SingleVisibilityConfigPropType,
    profile: SingleVisibilityConfigPropType,
    cases: SingleVisibilityConfigPropType,
    screenings: SingleVisibilityConfigPropType,
    eligibility: SingleVisibilityConfigPropType,
    forms: SingleVisibilityConfigPropType,
    uploads: SingleVisibilityConfigPropType,
    referrals: SingleVisibilityConfigPropType,
    resource: SingleVisibilityConfigPropType,
    carePlan: SingleVisibilityConfigPropType,
  }).isRequired,
  isCaseManagerRole: PropTypes.bool,
};

FacesheetSubHeader.defaultProps = {
  styles: {
    navHeaders: {
      fontSize: '16px',
    },
    referButton: {
      label: {
        color: 'white',
        fontSize: '12px',
      },
      icon: {
        paddingRight: '10px',
        height: '18px',
        width: '18px',
        fill: 'white',
      },
    },
  },
  clientRelationship: {},
  scrn7ScreeningsCompletionEnabled: false,
  dispatchCreateReferral: () => { },
  resourceListDetailEnhancements: false,
  hasSocialCareNavigationRole: false,
  isCaseManagerRole: false,
  isUpdatingRelationship: false,
  carePlanTabEnabled: false,
};

FacesheetSubHeader.contextTypes = {
  eventTracker: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  scrn7ScreeningsCompletionEnabled: scrn7ScreeningsCompletion(state),
  carePlanTabEnabled: cl2372SocialCarePlanTab(state),
  hasSocialCareNavigationRole: hasFeatureRole({
    employeeRoles: get(state, 'globalState.currentEmployee.roles', []),
    targetRoleKey: 'social_care_navigation',
  }),
  isCaseManagerRole: hasCaseManagerRole(state),
});

export default connect(mapStateToProps, {})(withAppCreateReferralContext(featureFlag(FacesheetSubHeader)));
